/**
 * Estilos dos blocos que acompanham TODA tela de relatório: carimbo de idade do
 * dado, avisos, filtros e estado bloqueado.
 *
 * Reaproveita o que já existe — as variáveis de espaçamento (`--sp-*`), de
 * fonte (`--fs-*`) e o `theme.colors` dos outros componentes. Não introduz tema
 * novo, não toca em `styles/global/*` nem em `styles/components/DynamicTable.ts`.
 */
import styled from "styled-components";

export const Bloco = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-8);
  padding: var(--sp-8) var(--sp-10);
  margin-bottom: var(--sp-10);
  border: 1px solid ${(props) => props.theme.colors.text}33;
  border-radius: 6px;
  background-color: #ffffffcc;
  font-size: var(--fs-sm);
  color: ${(props) => props.theme.colors.text};
`;

export const Icone = styled.span`
  font-family: "Material Icons";
  font-size: var(--fs-lg);
  line-height: 1;
`;

export const Texto = styled.span`
  line-height: 1.4;
`;

export const Forte = styled.strong`
  font-weight: 600;
`;

/** Selo curto: `parcial`, `reconstituído`, origem do dado. */
export const Selo = styled.span<{ tom?: "atencao" | "info" | "erro" }>`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-4);
  padding: 2px var(--sp-8);
  border-radius: 12px;
  font-size: var(--fs-xs);
  font-weight: 600;
  text-transform: lowercase;
  white-space: nowrap;
  color: ${(props) =>
    props.tom === "erro"
      ? "#be3028"
      : props.tom === "atencao"
      ? "#8f5e07"
      : "#1c7295"};
  background-color: ${(props) =>
    props.tom === "erro"
      ? "#be302814"
      : props.tom === "atencao"
      ? "#8f5e0714"
      : "#1c729514"};
  border: 1px solid
    ${(props) =>
      props.tom === "erro"
        ? "#be302840"
        : props.tom === "atencao"
        ? "#8f5e0740"
        : "#1c729540"};
`;

export const ListaDeAvisos = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  width: 100%;
`;

export const ItemDeAviso = styled.li`
  display: flex;
  gap: var(--sp-8);
  align-items: flex-start;
  line-height: 1.45;
`;

export const CodigoDoAviso = styled.code`
  font-size: var(--fs-xs);
  padding: 1px var(--sp-4);
  border-radius: 4px;
  background-color: #8f5e0714;
  color: #8f5e07;
  white-space: nowrap;
`;

export const BotaoDeDetalhe = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${(props) => props.theme.colors.selection};
  font-size: var(--fs-sm);
  text-decoration: underline;
  padding: 0;
`;

// ---------------------------------------------------------------- filtros

export const Filtros = styled.form`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--sp-10);
  padding: var(--sp-10);
  margin-bottom: var(--sp-10);
  border: 1px solid ${(props) => props.theme.colors.text}33;
  border-radius: 6px;
  background-color: #ffffffcc;
`;

export const Campo = styled.label`
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  font-size: var(--fs-xs);
  color: ${(props) => props.theme.colors.text};
`;

export const Entrada = styled.input`
  padding: var(--sp-4) var(--sp-8);
  border: 1px solid ${(props) => props.theme.colors.text}55;
  border-radius: 4px;
  font-size: var(--fs-sm);
  background-color: #ffffff;
  color: ${(props) => props.theme.colors.text};
`;

export const Selecao = styled.select`
  padding: var(--sp-4) var(--sp-8);
  border: 1px solid ${(props) => props.theme.colors.text}55;
  border-radius: 4px;
  font-size: var(--fs-sm);
  background-color: #ffffff;
  color: ${(props) => props.theme.colors.text};
  min-width: calc(var(--u) * 40);
`;

export const Botao = styled.button`
  padding: var(--sp-4) var(--sp-16);
  border: 1px solid ${(props) => props.theme.colors.selection};
  border-radius: 4px;
  background-color: ${(props) => props.theme.colors.selection};
  color: ${(props) => props.theme.colors.textOnPrimary};
  font-size: var(--fs-sm);
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

export const BotaoSecundario = styled(Botao)`
  background-color: transparent;
  color: ${(props) => props.theme.colors.text};
  border-color: ${(props) => props.theme.colors.text}55;
`;

export const PerguntaDaVisao = styled.p`
  width: 100%;
  margin: 0;
  font-size: var(--fs-sm);
  font-style: italic;
  color: ${(props) => props.theme.colors.text}bb;
`;

// -------------------------------------------------------- estado bloqueado

export const PainelBloqueado = styled.div`
  padding: var(--sp-20);
  border: 1px solid #8f5e0740;
  border-radius: 8px;
  background-color: #8f5e0708;
  color: ${(props) => props.theme.colors.text};
`;

export const TituloBloqueado = styled.h3`
  margin: 0 0 var(--sp-8) 0;
  font-size: var(--fs-lg);
  color: #8f5e07;
`;

export const MotivoBloqueado = styled.p`
  margin: 0 0 var(--sp-16) 0;
  line-height: 1.55;
`;

export const SubtituloBloqueado = styled.h4`
  margin: 0 0 var(--sp-8) 0;
  font-size: var(--fs-sm);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${(props) => props.theme.colors.text}aa;
`;

export const ListaDoQueFalta = styled.ul`
  margin: 0 0 var(--sp-20) var(--sp-20);
  padding: 0;
  line-height: 1.6;
`;

export const Diagnostico = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-10);
`;

export const Medida = styled.div`
  min-width: calc(var(--u) * 55);
  padding: var(--sp-8) var(--sp-10);
  border: 1px solid ${(props) => props.theme.colors.text}22;
  border-radius: 6px;
  background-color: #ffffffcc;
`;

export const ValorDaMedida = styled.div`
  font-size: var(--fs-2xl);
  font-weight: 700;
  line-height: 1.1;
  color: ${(props) => props.theme.colors.selection};
`;

export const RotuloDaMedida = styled.div`
  font-size: var(--fs-xs);
  margin-top: var(--sp-4);
  color: ${(props) => props.theme.colors.text}aa;
  line-height: 1.35;
`;

// ------------------------------------------------------------------- abas

export const Abas = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-4);
  margin-bottom: var(--sp-10);
  border-bottom: 1px solid ${(props) => props.theme.colors.text}22;
`;

export const Aba = styled.button<{ ativa?: boolean }>`
  padding: var(--sp-8) var(--sp-16);
  border: none;
  border-bottom: 2px solid
    ${(props) => (props.ativa ? props.theme.colors.selection : "transparent")};
  background: transparent;
  cursor: pointer;
  font-size: var(--fs-sm);
  font-weight: ${(props) => (props.ativa ? 600 : 400)};
  color: ${(props) =>
    props.ativa ? props.theme.colors.selection : props.theme.colors.text};
`;

export const Rodape = styled.div`
  margin-top: var(--sp-10);
  font-size: var(--fs-xs);
  color: ${(props) => props.theme.colors.text}99;
  line-height: 1.5;
`;
